'use client'

import Link from 'next/link'
import { Logo } from '@/components/logo'
import { Button } from '@/components/ui/button'
import { DotPattern } from '@/components/magicui/dot-pattern'
import { CircuitBoard, WifiOff, ServerCrash } from 'lucide-react'

// Enhanced shadow-border component for reusability
const ShadowBorder = ({ children, bgColor, shadowColor, className = '' }: {
  children: React.ReactNode;
  bgColor: string;
  shadowColor: string;
  className?: string;
}) => (
    <div className={`relative ${className}`}>
        <div className={`absolute top-2 left-2 w-full h-full ${shadowColor} border-4 border-black rounded-none`} />
        <div className={`relative w-full h-full ${bgColor} border-4 border-black`}>
            {children}
        </div>
    </div>
);

export default function NotFoundPage() {
    return (
        <div className="relative min-h-screen bg-white flex items-center justify-center p-4 overflow-hidden font-sans">
            <DotPattern
                width={30}
                height={30}
                cx={1.5}
                cy={1.5}
                cr={1}
                className="fill-neutral-300/80 [mask-image:radial-gradient(600px_circle_at_center,white,transparent)]"
            />

            {/* Decorative Glitch Elements with Shadow Border */}
            <div className="hidden lg:block absolute top-24 left-16 transform -rotate-12 animate-pulse">
                <ShadowBorder bgColor="bg-red-400" shadowColor="bg-purple-400">
                    <p className="font-black text-black text-lg uppercase px-4 py-2">ERR: 404</p>
                </ShadowBorder>
            </div>
            <div className="hidden lg:block absolute top-1/3 right-24 transform rotate-6">
                 <ShadowBorder bgColor="bg-green-400" shadowColor="bg-red-400">
                    <p className="font-black text-black text-lg uppercase px-4 py-2">NO_SIGNAL</p>
                </ShadowBorder>
            </div>
            <div className="hidden lg:block absolute bottom-1/4 left-1/4 transform rotate-3 animate-pulse delay-500">
                <ShadowBorder bgColor="bg-purple-400" shadowColor="bg-green-400">
                    <p className="font-black text-black text-lg uppercase px-4 py-2">NULL_ROUTE</p>
                </ShadowBorder>
            </div>
            <div className="hidden lg:block absolute bottom-16 right-1/3 transform -rotate-6">
                <ShadowBorder bgColor="bg-orange-400" shadowColor="bg-blue-400">
                    <p className="font-black text-black text-lg uppercase px-4 py-2">DISCONNECTED</p>
                </ShadowBorder>
            </div>

            <div className="relative z-10 flex flex-col items-center text-center">
                {/* Enhanced Glitched 404 Number with more layers */}
                <div className="relative mb-4">
                    <h1 className="absolute top-2.5 left-2.5 text-9xl md:text-[220px] font-black text-green-400 z-0 select-none">404</h1>
                    <h1 className="absolute -top-1 -left-1 text-9xl md:text-[220px] font-black text-purple-400 z-10 select-none">404</h1>
                    <h1 className="relative text-9xl md:text-[220px] font-black text-black z-20 select-none">404</h1>
                    <div 
                        className="absolute top-1 left-1 text-9xl md:text-[220px] font-black text-pink-500 z-20 mix-blend-multiply select-none"
                        aria-hidden="true"
                    >
                        404
                    </div>
                </div>

                {/* Main Message Box with Shadow Border */}
                <div className="relative transform -rotate-1 hover:rotate-0 transition-transform duration-200 mb-8 max-w-lg">
                    <div className="absolute top-2 left-2 w-full h-full bg-red-500 border-8 border-black" />
                    <div className="relative bg-blue-400 border-8 border-black p-6 md:p-8">
                        <div className="flex items-center justify-center gap-4 mb-4">
                            <ServerCrash className="w-8 h-8 md:w-10 md:h-10 text-black" />
                            <h2 className="text-2xl md:text-4xl font-black text-black uppercase">
                                System Glitch
                            </h2>
                        </div>
                        <p className="text-lg md:text-xl font-bold text-black">
                           You've fallen out of the Social Loop. The page you're looking for has been disconnected or doesn't exist.
                        </p>
                    </div>
                </div>

                {/* Call to Action */}
                <Link href="/" passHref>
                    <Button
                        className="bg-black text-white font-black text-lg p-4 border-4 border-black hover:bg-white hover:text-black transition-colors duration-150 transform rotate-2 hover:rotate-0 group will-change-transform"
                    >
                        RE-ENTER THE LOOP
                        <CircuitBoard className="ml-3 h-6 w-6 transition-transform group-hover:rotate-180" />
                    </Button>
                </Link>
            </div>
            
            {/* Corner Logo */}
            <div className="absolute top-6 left-6 z-30">
                <Link href="/" aria-label="go home">
                    <div className="relative transform -rotate-3 hover:rotate-0 transition-transform duration-200">
                        <div className="absolute top-1.5 left-1.5 w-full h-full bg-blue-400 border-4 border-black"/>
                        <div className="relative bg-black border-4 border-black p-3">
                            <Logo className="h-6 w-auto text-white" uniColor />
                        </div>
                    </div>
                </Link>
            </div>
        </div>
    )
}